import React from 'react';
import { TrendingUp, Calendar, Percent, DollarSign, BarChart2, AlertCircle, ArrowDown, ArrowUp } from 'lucide-react';
import ToolResult, { ResultItem } from '../../../../../components/ui/ToolResult';
import { formatNumber, formatCurrency } from '../../../../../utils/formatting';
import Button from '../../../../../components/ui/Button';

/**
 * Компонент отображения результатов расчета инфляции
 */
const ResultsOverview = ({ results, formData, showDetailedAnalysis, setShowDetailedAnalysis }) => {
  if (!results) return null;

  // Определение текста для типа расчета
  const calculationTypeText = results.calculationType === 'appreciation' 
    ? 'Рост стоимости с учетом инфляции' 
    : 'Обесценивание покупательной способности';

  // Определение цвета и значков изменения
  const changeColor = results.calculationType === 'appreciation' ? 'emerald' : 'rose';
  const ChangeIcon = results.calculationType === 'appreciation' ? ArrowUp : ArrowDown;

  // Читаемое название категории
  const categoryNames = {
    general: "Общая инфляция",
    food: "Продукты питания",
    housing: "Жилье и коммунальные услуги",
    transport: "Транспорт",
    medical: "Медицинские услуги",
    education: "Образование",
    clothing: "Одежда и обувь",
    entertainment: "Развлечения",
    electronics: "Электроника",
    furniture: "Мебель и бытовая техника"
  };
  
  const categoryName = categoryNames[results.itemCategory] || results.itemCategory;

  return (
    <ToolResult title="Результаты расчета влияния инфляции">
      {/* Ключевые метрики */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-6">
        {/* Изменение стоимости */}
        <div className={`rounded-lg bg-gradient-to-br from-${changeColor}-50 to-${changeColor}-100 dark:from-${changeColor}-900/30 dark:to-${changeColor}-900/20 p-5 border border-${changeColor}-100 dark:border-${changeColor}-800/50 shadow-sm`}>
          <h3 className={`text-base font-medium text-${changeColor}-700 dark:text-${changeColor}-300 mb-1.5 flex items-center gap-1.5`}>
            <ChangeIcon className="w-4 h-4"/>
            {results.calculationType === 'appreciation' ? 'Выросло до' : 'Снизилось до'}
          </h3>
          <div className="flex items-baseline flex-wrap gap-x-2">
            <span className={`text-3xl font-bold text-${changeColor}-800 dark:text-${changeColor}-200`} aria-live="polite">
              {formatCurrency(results.finalValue)}
            </span>
            <span className={`text-sm font-medium text-${changeColor}-600 dark:text-${changeColor}-400`}>
              ({formatNumber(Math.abs(results.changePercent), 1)}%)
            </span>
          </div>
          <p className={`text-xs text-${changeColor}-600 dark:text-${changeColor}-400 mt-1`}>
            {results.calculationType === 'appreciation' 
              ? 'Сумма с учетом инфляции' 
              : 'Покупательная способность'}
          </p>
        </div>

        {/* Накопленная инфляция */}
        <div className="rounded-lg bg-gradient-to-br from-indigo-50 to-purple-50 dark:from-indigo-900/30 dark:to-purple-900/30 p-5 border border-indigo-100 dark:border-indigo-800/50 shadow-sm">
          <h3 className="text-base font-medium text-indigo-700 dark:text-indigo-300 mb-1.5 flex items-center gap-1.5">
            <Percent className="w-4 h-4"/>
            Накопленная инфляция
          </h3>
          <div className="flex items-baseline flex-wrap gap-x-2">
            <span className="text-3xl font-bold text-indigo-800 dark:text-indigo-200" aria-live="polite">
              {formatNumber(results.cumulativeInflation, 1)}%
            </span>
            <span className="text-sm font-medium text-indigo-600 dark:text-indigo-400">
              за {results.endYear - results.startYear} {pluralize(results.endYear - results.startYear, 'год', 'года', 'лет')}
            </span>
          </div>
          <p className="text-xs text-indigo-600 dark:text-indigo-400 mt-1">
            Категория: {categoryName}
          </p>
        </div>
      </div>

      {/* Детализированные результаты */}
      <div className="space-y-3 mb-6">
        <ResultItem 
          label="Начальная сумма" 
          value={formatCurrency(results.initialAmount)} 
          color="indigo" 
          icon={<DollarSign className="w-4 h-4"/>}
        />
        
        <ResultItem 
          label="Конечная сумма" 
          value={formatCurrency(results.finalValue)} 
          color="indigo" 
          icon={<DollarSign className="w-4 h-4"/>}
        />
        
        <ResultItem 
          label="Абсолютное изменение" 
          value={formatCurrency(Math.abs(results.changeAbsolute))} 
          color={changeColor} 
          icon={<ChangeIcon className="w-4 h-4"/>} 
        />
        
        <ResultItem 
          label="Относительное изменение" 
          value={`${formatNumber(Math.abs(results.changePercent), 1)}%`} 
          color={changeColor}
          icon={<Percent className="w-4 h-4"/>} 
        />

        <ResultItem 
          label="Период расчета" 
          value={`${results.startYear} — ${results.endYear} (${results.endYear - results.startYear} ${pluralize(results.endYear - results.startYear, 'год', 'года', 'лет')})`} 
          icon={<Calendar className="w-4 h-4"/>} 
        />
        
        <ResultItem 
          label="Тип расчета" 
          value={calculationTypeText} 
          icon={<BarChart2 className="w-4 h-4"/>}
        />
        
        {formData.considerTaxes && results.calculationType === 'appreciation' && (
          <>
            <ResultItem 
              label="Сумма налога (НДФЛ)" 
              value={formatCurrency(results.taxAmount)} 
              color="red" 
            />
            
            <ResultItem 
              label="Сумма после налогообложения" 
              value={formatCurrency(results.afterTaxValue)} 
              color="emerald" 
            />
          </>
        )}

        {/* Сравнение с активами */}
        {formData.compareToBenchmarks && Object.keys(results.benchmarkResults).length > 0 && (
          <>
            <div className="border-t border-gray-200 dark:border-gray-700 my-4"></div>
            <h4 className="font-medium text-gray-900 dark:text-white text-base mb-2">
              Сравнение с инвестиционными активами
            </h4>
            
            {Object.entries(results.benchmarkResults).map(([assetId, assetData]) => {
              const performanceRatio = assetData.performanceVsInflation;
              let performanceText, performanceColor;
              
              if (performanceRatio > 1.05) {
                performanceText = "Лучше инфляции";
                performanceColor = "emerald";
              } else if (performanceRatio < 0.95) {
                performanceText = "Хуже инфляции";
                performanceColor = "rose";
              } else {
                performanceText = "На уровне инфляции";
                performanceColor = "yellow";
              }
              
              // Имена активов
              const assetNames = {
                usd: "Доллар США",
                eur: "Евро",
                gold: "Золото",
                realEstate: "Недвижимость",
                stocks: "Фондовый рынок",
                deposits: "Банковские вклады"
              };
              
              return (
                <div key={assetId} className="flex justify-between items-center border-b border-gray-100 dark:border-gray-800 py-2 last:border-0">
                  <div className="flex items-center gap-2">
                    <div className={`w-3 h-3 rounded-full bg-${performanceColor}-500`}></div>
                    <span className="font-medium text-gray-700 dark:text-gray-300">{assetNames[assetId] || assetId}</span>
                  </div>
                  <div className="flex flex-col items-end">
                    <span className="font-medium text-gray-900 dark:text-white">{formatCurrency(assetData.investmentValue)}</span>
                    <span className={`text-xs text-${performanceColor}-600 dark:text-${performanceColor}-400`}>
                      {performanceText} (×{formatNumber(performanceRatio, 2)})
                    </span>
                  </div>
                </div>
              );
            })}
          </>
        )}

        {/* Информация о рисках инфляции */}
        <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4 mt-2 border border-gray-200 dark:border-gray-700">
          <div className="flex items-start gap-3">
            <div className="mt-0.5">
              <AlertCircle className="w-5 h-5 text-amber-500" />
            </div>
            <div>
              <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-1">Инфляционные риски</h4>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Высокая инфляция может существенно снизить покупательную способность денег. Для защиты от инфляции 
                рекомендуется диверсифицировать сбережения с помощью различных инструментов инвестирования.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Кнопка для показа детального анализа */}
      <div className="text-center mt-4">
        <Button
          onClick={() => setShowDetailedAnalysis(!showDetailedAnalysis)}
          variant="outline"
          size="md"
        >
          {showDetailedAnalysis 
            ? "Скрыть детальный анализ" 
            : "Показать детальный анализ по годам"}
        </Button>
      </div>
    </ToolResult>
  );
};

// Вспомогательная функция для склонения слова в зависимости от числа
const pluralize = (count, one, few, many) => {
  const lastDigit = count % 10;
  const lastTwoDigits = count % 100;
  
  if (lastTwoDigits >= 11 && lastTwoDigits <= 19) return many;
  if (lastDigit === 1) return one;
  if (lastDigit >= 2 && lastDigit <= 4) return few;
  return many;
};

export default ResultsOverview;